import React, { useState } from "react";
import { signIn } from "next-auth/react";
import { useFormik } from "formik";
import { useRouter } from "next/navigation";
import Link from "next/link";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Email, Phone } from "@mui/icons-material";
import emailValidator from "email-validator";
import { phone as phoneValidator } from "phone";
import { random, get, isEmpty, trim } from "lodash";
import { encrypt, md5 } from "../../../core/utils/crypo-utils";
import { logger } from "../../../core";
import styles from "../../index.module.scss";

interface LoginFormProps {
  onLoginSuccess?: (data: any) => void;
  onShowOTP?: (userId: string) => void;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  className?: string;
  accessTo?: string;
}

export default function LoginForm({ 
  onLoginSuccess, 
  onShowOTP, 
  loading = false, 
  setLoading = () => {},
  className = "",
  accessTo
}: LoginFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const getLocalData = (userId: string): any => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(md5(trim(userId)));
    }
    return null;
  };

  const setLocalData = (key: string, data: any) => {
    localStorage.setItem(md5(trim(key)), JSON.stringify(data));
  };

  const processPath = (type: string, data: any) => {
    let path = "";
    logger.log({ type, data });
    if (type === "customer") {
      path = "/customers";
    } else if (type === "vendor") {
      path = "/vendors";
    } else if (type === "admin") {
      path = "/admin";
    } else {
      path = "/login";
    }

    if (path) {
      router.push(path + "?&t=" + new Date().getTime().toString());
    }
  };

  const formik = useFormik<any>({
    initialValues: {
      userId: "",
      password: "",
      error: "",
    } as any,

    validate: (values: any) => {
      const errors: any = {
        userId: undefined,
        password: undefined,
        error: undefined,
      };

      if (!values.userId) {
        errors.userId = "Phone/Email Required";
      }

      if (!values.password) {
        errors.password = "Password Required";
      }

      if (values.userId) {
        if (
          emailValidator.validate(values.userId) == false &&
          phoneValidator(values.userId).isValid == false
        ) {
          errors.userId = "Invalid Phone/Email";
        }
      }
      return errors.userId || errors.password ? errors : undefined;
    },

    onSubmit: async (values, formikHelpers) => {
      if (values.userId) {
        const data: any = {};

        if (emailValidator.validate(values.userId)) {
          data.email = values.userId;
        }

        if (phoneValidator(values.userId).isValid) {
          data.phoneNumber = values.userId;
        }

        if (phoneValidator(values.userId)) {
          data.reference = random(3000000, 300000000) + "";
        }

        const localcreds = JSON.parse(
          (getLocalData(values.userId) as any) || "{}"
        );

        const { sec } = localcreds;

        if (sec) {
          setLoading(true);
          const result = await fetch(`/api/auth/v2/login`, {
            method: "POST",
            body: JSON.stringify({
              ...data,
              pin: encrypt(values.password, sec),
            }),
          });
          setLoading(false);

          if (result.status == 200) {
            const resultData = await result.json();
            const statusCode = get(resultData, "header.statusCode");
            if (statusCode == "OK") {
              const type = get(resultData, "body.response.type");
              if (onLoginSuccess) {
                onLoginSuccess(resultData);
              } else {
                processPath(type, {});
                if (window && window.location) {
                  // if router did't work do a hard refresh
                  // This is an existing but sometime rout.push not working
                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                }
              }
            } else if (statusCode == "INACTIVE_ACCOUNT") {
              logger.log({ isInactive: true });
              formikHelpers.setFieldError("error", "Account is inactive");
            } else {
              const message = get(resultData, "header.statusCode");
              formikHelpers.setFieldError("error", message);
            }
            return;
          } else if (result.status >= 400 && result.status < 500) {
            const errorData = await result.json();
            const statusCode = get(errorData, "header.statusCode");
            if (statusCode == "INACTIVE_ACCOUNT") {
              logger.log({ isInactive: true });
              formikHelpers.setFieldError("error", "Account is inactive");
              return;
            } else if (statusCode == "INVALID_SECURITY_SIGNATURE") {
              logger.log("continue with otp flow ", { statusCode });
            } else {
              formikHelpers.setFieldError("error", get(errorData, "header.statusCode"));
              return;
            }
          } else {
            logger.log("no handling defined");
            return;
          }
        }

        setLoading(true);
        const result = await fetch(`/api/auth/v1/otp`, {
          method: "POST",
          body: JSON.stringify(data),
        });
        setLoading(false);
        if (result.ok) {
          const data = await result.json();
          if (onShowOTP) {
            onShowOTP(values.userId);
          }
        }
      }
    },
  });

  const getErrorMessage = (errorStatus: string) => {
    return (
      <div className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
        {errorStatus}
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md mx-auto ${className}`}>
      {/* Form Header */}
      <div className="flex justify-center flex-col items-center mb-10">
        <div className={styles.logo}>
         
        </div>
        <p className="text-gray-600">Sign in to your account</p>
      </div>

      {/* Login Form */}
      <form onSubmit={formik.handleSubmit} className="space-y-6">
        {/* Email/Phone Field */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Email or Phone Number
          </label>
          <div className="relative group">
            <TextField
              onChange={formik.handleChange}
              size="medium"
              id="userId"
              name="userId"
              type="text"
              placeholder="Enter your email or phone"
              className="w-full"
              fullWidth={true}
              error={
                Boolean(formik.errors["userId"])
              }
              helperText={
                formik.touched.userId && typeof formik.errors.userId === 'string'
                  ? formik.errors.userId
                  : undefined
              }
              value={formik.values.userId}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {emailValidator.validate(formik.values.userId) ? (
                      <Email className="h-5 w-5 text-blue-500" />
                    ) : (
                      <Phone className="h-5 w-5 text-blue-500" />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Password
          </label>
          <FormControl fullWidth>
            <OutlinedInput
              onChange={formik.handleChange}
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              size="medium"
              placeholder="Enter your password"
              className="w-full"
              fullWidth={true}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{ color: '#6b7280' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              error={
                formik.touched.password &&
                Boolean(formik.errors.password)
              }
              value={formik.values.password}
              sx={{
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px',
                },
              }}
            />
            <FormHelperText
              error={
                formik.touched.password &&
                Boolean(formik.errors.password)
              }
              sx={{ color: '#6b7280' }}
            >
              {formik.errors.password as string}
            </FormHelperText>
          </FormControl>
        </div>

        {/* Forgot Password */}
        <div className="flex items-center justify-end">
          <Link
            href="/recover-password"
            className="text-sm !text-sky-600 hover:!text-sky-700 font-medium transition-colors duration-200 cursor-pointer"
          >
            Forgot password?
          </Link>
        </div>

        {/* Error Message */}
        {formik.errors.error && getErrorMessage(formik.errors.error as string)}

        {/* Login Button */}
        <Button 
          variant="contained" 
          disableElevation 
          type="submit"
          fullWidth
          size="large"
          className="h-14 text-lg font-bold bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} className="text-white" />
          ) : (
            "Sign In"
          )}
        </Button>

        <div className="flex items-center gap-3 pt-2">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs uppercase tracking-wide text-gray-400">
            Or continue with
          </span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <Button
          variant="outlined"
          type="button"
          fullWidth
          size="large"
          onClick={() => signIn("google", {
             callbackUrl: "/api/auth" ,
             baseUrl: process.env.NEXTAUTH_URL
            })}
          className="h-14 text-base font-semibold rounded-xl"
          sx={{
            borderColor: "#e5e7eb",
            color: "#111827",
            textTransform: "none",
            backgroundColor: "#ffffff",
            "&:hover": {
              borderColor: "#d1d5db",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          <span className="mr-3 inline-flex h-5 w-5 items-center justify-center">
            <svg
              viewBox="0 0 48 48"
              aria-hidden="true"
              focusable="false"
              className="h-5 w-5"
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.7 1.22 9.18 3.6l6.84-6.84C35.94 2.38 30.37 0 24 0 14.64 0 6.6 5.38 2.7 13.22l7.98 6.2C12.6 13.05 17.9 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.14 24.5c0-1.62-.15-3.18-.42-4.68H24v8.86h12.42c-.54 2.9-2.16 5.36-4.6 7.02l7.08 5.5c4.14-3.82 7.24-9.44 7.24-16.7z"
              />
              <path
                fill="#FBBC05"
                d="M10.68 28.42c-.52-1.56-.82-3.22-.82-4.92s.3-3.36.82-4.92l-7.98-6.2C.94 15.84 0 19.7 0 23.5s.94 7.66 2.7 10.12l7.98-5.2z"
              />
              <path
                fill="#34A853"
                d="M24 47c6.37 0 11.73-2.1 15.64-5.7l-7.08-5.5c-1.96 1.32-4.48 2.1-8.56 2.1-6.1 0-11.4-3.55-13.32-8.38l-7.98 5.2C6.6 42.62 14.64 47 24 47z"
              />
              <path fill="none" d="M0 0h48v48H0z" />
            </svg>
          </span>
          Sign in with Google
        </Button>

        {/* Sign Up Link */}
        <div className="text-center pt-6">
          <span className="text-gray-600">Don't have an account? </span>
          <Link
            href="/signup"
            className="!text-sky-600 hover:!text-sky-700 font-semibold transition-colors duration-200"
          >
            Sign up
          </Link>
        </div>
      </form>
    </div>
  );
}
